import { useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useEffect, useRef } from "react";
import { streamUserEvents, type UserEvent, type UserEventName, type UserEventOf } from "@/api";
import { reportError } from "@/telemetry/telemetry";
import { receiveNotification, receiveUnreadCount, refreshNotifications } from "./notificationCache";

type Listener = (event: UserEvent) => void;
type Subscribe = (listener: Listener) => () => void;

const UserEventsContext = createContext<Subscribe | null>(null);

/**
 * Opens the per-user event stream once for the signed-in shell, keeps the
 * notification caches current, and lets features listen to the other events
 * (chat titles, operation status) with useUserEvent().
 */
export function UserEventsProvider({
  children,
  onNotification,
}: Readonly<{ children: ReactNode; onNotification?: (event: UserEventOf<"notification">) => void }>) {
  const queryClient = useQueryClient();
  const listeners = useRef(new Set<Listener>());
  const onNotificationRef = useRef(onNotification);
  onNotificationRef.current = onNotification;

  useEffect(() => {
    const controller = new AbortController();
    let connections = 0;
    (async () => {
      for await (const event of streamUserEvents({ signal: controller.signal })) {
        if (event.event === "heartbeat" && event.data.status === "connected") {
          // Anything may have changed while we were disconnected.
          connections += 1;
          if (connections > 1) refreshNotifications(queryClient);
        } else if (event.event === "notification") {
          receiveNotification(queryClient, event.data);
          onNotificationRef.current?.(event);
        } else if (event.event === "unread_count") {
          receiveUnreadCount(queryClient, event.data.count);
        }
        for (const listener of listeners.current) listener(event);
      }
    })().catch((error: unknown) => reportError(error, { source: "userStream" }));
    return () => controller.abort();
  }, [queryClient]);

  const subscribe = useRef<Subscribe>((listener) => {
    listeners.current.add(listener);
    return () => listeners.current.delete(listener);
  }).current;

  return <UserEventsContext.Provider value={subscribe}>{children}</UserEventsContext.Provider>;
}

/** Run `handler` for each `name` event on the user stream. */
export function useUserEvent<K extends UserEventName>(name: K, handler: (event: UserEventOf<K>) => void) {
  const subscribe = useContext(UserEventsContext);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event) => {
      if (event.event === name) handlerRef.current(event as UserEventOf<K>);
    });
  }, [subscribe, name]);
}
